<!-- eslint-disable vue/valid-v-slot -->
<template>
  <div>
    <div class="flex w-full items-center gap-2">
      <div>
        <v-btn-toggle :disabled="loadingState === 'loading'" v-model="searchType" variant="outlined" mandatory>
          <v-btn value="ByNumber" key="ByNumber">
            <v-icon>mdi-numeric</v-icon>
          </v-btn>
          <v-btn value="ByDate" key="ByDate">
            <v-icon>mdi-calendar</v-icon>
          </v-btn>
        </v-btn-toggle>
      </div>

      <v-window v-model="searchType" class="w-full">
        <v-window-item key="ByDate" value="ByDate">
          <v-text-field
            hide-details
            :disabled="loadingState === 'loading' || materialStore.loadingState !== 'success'"
            :label="i18n.global.t('global.searchTimestamp')"
            readonly
            variant="solo-filled"
            rounded="md"
            :model-value="`${date.format(i18n.global.t('global.dateFormat'))} (${getDateDiffText(date)})`"
            prepend-inner-icon="mdi-calendar"
          >
            <template #append-inner="{ blur }">
              <v-btn
                :loading="loadingState === 'loading'"
                @click="
                  async () => {
                    blur();
                    onSearchByDateClick();
                  }
                "
                color="primary"
                prepend-icon="mdi-magnify"
                >{{ i18n.global.t("global.search") }}</v-btn
              >
            </template>
            <v-menu
              :close-on-content-click="false"
              activator="parent"
              :model-value="loadingState == 'loading' ? false : showDateSelectorMenu"
              @update:model-value="(val) => (showDateSelectorMenu = val)"
            >
              <template #default="{ isActive }">
                <v-card class="w-min">
                  <v-date-picker
                    v-model="date"
                    :show-adjacent-months="true"
                    color="primary"
                    :allowed-dates="allowedDates"
                    :min="dayjs().subtract(2, 'months').format('YYYY-MM-DD')"
                    :max="dayjs().format('YYYY-MM-DD')"
                  ></v-date-picker>
                  <template #actions>
                    <v-btn
                      color="primary"
                      variant="elevated"
                      @click="
                        () => {
                          isActive.value = false;
                          onSearchByDateClick();
                        }
                      "
                      prepend-icon="mdi-magnify"
                      >{{ i18n.global.t("global.search") }}</v-btn
                    >
                  </template>
                </v-card>
              </template>
            </v-menu>
          </v-text-field>
        </v-window-item>
        <v-window-item key="ByNumber" value="ByNumber">
          <v-text-field
            :disabled="loadingState === 'loading' || materialStore.loadingState !== 'success'"
            hide-details
            clearable
            v-on:keyup.enter="onSearchByNumberClick"
            variant="solo-filled"
            :label="i18n.global.t('app.cancelSerials.info.name')"
            :model-value="searchSerialNumber || null"
            @update:model-value="(val) => (searchSerialNumber = val ?? '')"
          >
            <template #append-inner>
              <div>
                <v-btn
                  @click="onSearchByNumberClick"
                  :loading="loadingState === 'loading'"
                  color="primary"
                  prepend-icon="mdi-magnify"
                  >{{ i18n.global.t("global.search") }}</v-btn
                >
              </div>
            </template>
          </v-text-field>
        </v-window-item>
      </v-window>
    </div>
    <div class="mt-8">
      <v-data-table
        v-model="selectedSerials"
        :loading="loadingState === 'loading'"
        show-select
        :hover="loadingState !== 'loading'"
        select
        item-value="raw"
        :headers="[
          {
            title: i18n.global.t('app.cancelSerials.info.name'),
            key: 'sn',
            sortRaw(a, b) {
              return a.sn.serial > b.sn.serial ? 1 : -1;
            },
          },
          {
            title: i18n.global.t('global.date'),
            key: 'date',
            align: 'end',
          },
        ]"
        :items="
          serialSet.map((e) => ({
            sn: { serial: e.Serialnumber, type: e.Type },
            date: e.Timestamp,
            raw: e,
          }))
        "
      >
        <template v-slot:item.sn="{ value }: { value: { serial: string, type: string } }">
          <v-list-item
            class="px-0 min-w-40"
            :title="value.serial"
            :subtitle="value.type || i18n.global.t('global.noDescription')"
          ></v-list-item>
        </template>
        <template v-slot:item.date="{ value }">
          <v-list-item
            class="px-0 min-w-32"
            :subtitle="`${getDateDiffText(Number(value))} (${formatUNIX(value)})`"
          ></v-list-item>
        </template>
        <template v-slot:loading>
          <div v-for="index in 4" :key="index">
            <div class="flex justify-between flex-row">
              <div class="flex">
                <v-skeleton-loader type="avatar"></v-skeleton-loader>
                <v-skeleton-loader class="min-w-64" type="list-item-two-line"></v-skeleton-loader>
              </div>
              <v-skeleton-loader class="min-w-36" type="text@1"></v-skeleton-loader>
            </div>
            <v-skeleton-loader class="min-w-36" type="divider"></v-skeleton-loader>
          </div>
        </template>
      </v-data-table>
      <div class="" v-if="loadingState === 'success'">
        <v-btn
          :loading="cancelState === 'loading'"
          prepend-icon="mdi-delete"
          color="primary"
          :disabled="disableButton"
          @click="onCancelSerialsClick"
        >
          {{ i18n.global.t("app.cancelSerials.info.cancel") }}
        </v-btn>
      </div>
    </div>
    <v-snackbar v-model="showCancelSuccess" :timeout="5000">
      {{ i18n.global.t("app.cancelSerials.loadingCancel.arrived") }}
      <template v-slot:actions>
        <v-btn color="primary" variant="text" @click="showCancelSuccess = false">
          {{ i18n.global.t("global.close") }}
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script setup lang="ts">
// modules
import { computed, ref, Ref, onMounted, watch } from "vue";
// helpers
import { formatSAPDate, formatUNIX, getDateDiffText } from "@/helpers/dateHelper";
import { i18n } from "@/locales/index";
import * as ApiResponse from "@/api-client/responses";
import dayjs from "dayjs";
// instances
import { ApiClient } from "@/api-client";
import { useApplicationStore, useMaterialStore, useSupplierStore, useSystemStore } from "@/store";

// get instances
const systemStore = useSystemStore();
const supplierStore = useSupplierStore();
const materialStore = useMaterialStore();
const applicationStore = useApplicationStore();
const apiClient = ApiClient.getInstance();

const searchType = ref<"ByDate" | "ByNumber">("ByNumber");
const showDateSelectorMenu = ref(false);

const searchSerialNumber: Ref<string> = ref("");

const initialDate = dayjs().hour(0).minute(0).second(0).millisecond(0);
const date = ref(initialDate);
const allowedDates = (val: unknown) => {
  return true;
};

const showCancelSuccess = ref(false);

type loadingStateType = "unset" | "loading" | "failed" | "success";
const serialSet = ref<ApiResponse.Serials.SerialSet[]>([]);
const loadingState = ref<loadingStateType>("unset");
const abortController = ref(new AbortController());

const selectedSerials = ref<ApiResponse.Serials.SerialSet[]>([]);
const cancelState = ref<loadingStateType>("unset");

const disableButton = computed(() => {
  if (selectedSerials.value.length > 0) return false;
  return true;
});

materialStore.onAbort.addListener("abort", () => {
  abortController.value.abort();
  abortController.value = new AbortController();
  serialSet.value = [];
});

// get serials
watch(
  () => materialStore.loadingState,
  (state) => {
    if (state === "loading") {
      loadingState.value = "loading";
      return;
    }
    if (state === "failed") {
      loadingState.value = "failed";
      serialSet.value = [];
      return;
    }
    if (state !== "success") return;

    onSearchByNumberClick();
  }
);

async function onCancelSerialsClick() {
  if (selectedSerials.value.length <= 0) return;

  const serials = selectedSerials.value;

  cancelState.value = "loading";

  const serialNumbers: Array<string> = serials.map((x) => {
    return x.Serialnumber;
  });

  let response;

  if (!supplierStore.selectedSupplier || !systemStore.selectedSystem || !materialStore.selectedMatNr) {
    console.warn(`No Supplier selected!`);
    return;
  }

  try {
    response = await apiClient.cancelSerials(
      materialStore.selectedMatNr,
      supplierStore.selectedSupplier.SupplierId,
      systemStore.selectedSystem.supplierId,
      systemStore.selectedSystem.system,
      serialNumbers
    );
  } catch (error: unknown) {
    cancelState.value = "failed";
    applicationStore.addAlert({
      key: `app.cancelSerials.loadingCancel.error`,
      keyData: {
        message: error instanceof Error ? error.message : String(error),
      },
      type: "Alert",
    });
    return;
  }

  cancelState.value = "success";

  if (response) {
    applicationStore.addAlert({
      key: "app.cancelSerials.loadingCancel.arrived",
      type: "Success",
    });
  }

  showCancelSuccess.value = true;

  onSearchByNumberClick();
}

async function onSearchByNumberClick() {
  await searchSerials(null, null, searchSerialNumber.value, "ByNumber");
}

async function onSearchByDateClick() {
  await searchSerials(formatSAPDate(date.value.toDate()), formatSAPDate(new Date()), null, "ByDate");
}

async function searchSerials(
  startDate: string | null,
  endDate: string | null,
  serialNumber: string | null,
  type: "ByDate" | "ByNumber"
) {
  loadingState.value = "loading";
  showDateSelectorMenu.value = false;
  serialSet.value = [];
  selectedSerials.value = [];

  if (!supplierStore.selectedSupplier || !systemStore.selectedSystem || !materialStore.selectedMatNr) {
    console.warn(`No Supplier selected!`);
    return;
  }

  let response;
  try {
    if (type == "ByDate") {
      response = await apiClient.getSerialSetByDate(
        materialStore.selectedMatNr,
        supplierStore.selectedSupplier.SupplierId,
        systemStore.selectedSystem.supplierId,
        systemStore.selectedSystem.system,
        startDate,
        endDate,
        abortController.value.signal
      );
    } else {
      response = await apiClient.getSerialSetByNumber(
        materialStore.selectedMatNr,
        supplierStore.selectedSupplier.SupplierId,
        systemStore.selectedSystem.supplierId,
        systemStore.selectedSystem.system,
        serialNumber,
        abortController.value.signal
      );
    }
  } catch (error: unknown) {
    if (error instanceof Error && error?.name == "AbortError") return;

    applicationStore.addAlert({
      key: `app.cancelSerials.loadingData.error`,
      keyData: {
        message: error instanceof Error ? error.message : String(error),
      },
      type: "Alert",
    });
    loadingState.value = "failed";
  }
  showDateSelectorMenu.value = false;
  serialSet.value = response?.serialset ?? [];
  loadingState.value = loadingState.value === "failed" ? "failed" : "success";

  if (response?.limit) {
    applicationStore.addAlert({
      key: "app.cancelSerials.loadingData.limit",
      type: "Warning",
    });
  }
}
</script>

<style scoped lang="scss"></style>
